<template>
    <div
        class="info-tooltip"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
        @click.stop="toggleTooltip"
        ref="iconRef"
    >
        <svg class="info-tooltip__icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
            <path d="M12 17V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            <circle cx="12" cy="7" r="1" fill="currentColor"/>
        </svg>

        <Teleport to="body">
            <transition name="fade">
                <div
                    v-if="isVisible"
                    class="info-tooltip__body"
                    :style="tooltipStyles"
                    ref="tooltipRef"
                    @click.stop
                >
                    {{ text }}
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script>
export default {
    name: 'InfoLabel',
    props: {
        text: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            uid: 'tooltip-' + Math.random().toString(36).substr(2, 9),
            isVisible: false,
            top: 0,
            left: 0,
            isTouchDevice: false,
        };
    },
    computed: {
        tooltipStyles() {
            return {
                top: `${this.top}px`,
                left: `${this.left}px`,
            };
        },
    },
    mounted() {
        this.isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

        window.addEventListener('click', this.handleOutsideClick);
        window.addEventListener('scroll', this.handleScroll, true);
        window.addEventListener('tooltip-opened', this.handleOtherTooltipOpened);
    },
    beforeUnmount() {
        window.removeEventListener('click', this.handleOutsideClick);
        window.removeEventListener('scroll', this.handleScroll, true);
        window.removeEventListener('tooltip-opened', this.handleOtherTooltipOpened);
    },
    methods: {
        onMouseEnter() {
            if (this.isTouchDevice) return;
            this.show();
        },
        onMouseLeave() {
            if (this.isTouchDevice) return;
            this.hide();
        },
        toggleTooltip() {
            if (this.isVisible) {
                this.hide();
            } else {
                this.show();
            }
        },
        show() {
            window.dispatchEvent(new CustomEvent('tooltip-opened', {detail: this.uid}));
            this.isVisible = true;
            this.$nextTick(() => {
                this.calculatePosition();
            });
        },
        hide() {
            this.isVisible = false;
        },
        handleOtherTooltipOpened(event) {
            if (event.detail !== this.uid) {
                this.hide();
            }
        },
        handleOutsideClick(e) {
            if (this.isVisible && this.$refs.iconRef && !this.$refs.iconRef.contains(e.target)) {
                this.hide();
            }
        },
        handleScroll() {
            if (this.isVisible) {
                this.hide();
            }
        },
        calculatePosition() {
            const icon = this.$refs.iconRef;
            const tooltip = this.$refs.tooltipRef;

            if (!icon || !tooltip) return;

            const iconRect = icon.getBoundingClientRect();
            const tooltipRect = tooltip.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const padding = 10;

            this.top = iconRect.top - tooltipRect.height - 10;
            if (this.top < 0) {
                this.top = iconRect.bottom + 10;
            }

            let calculatedLeft = iconRect.left + (iconRect.width / 2) - (tooltipRect.width / 2);

            if (calculatedLeft < padding) {
                calculatedLeft = padding;
            }
            if (calculatedLeft + tooltipRect.width > viewportWidth - padding) {
                calculatedLeft = viewportWidth - tooltipRect.width - padding;
            }

            this.left = calculatedLeft;
        },
    },
};
</script>

<style scoped>
.info-tooltip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
    cursor: pointer;
    position: relative;
    vertical-align: middle;
}

.info-tooltip .info-tooltip__icon {
    display: block;
    width: 18px;
    height: 18px;
    color: #ffffff;
    user-select: none;
    background-color: #8189a1;
    border-radius: 50%;
    transition: all 0.2s ease;
}

.info-tooltip:hover .info-tooltip__icon,
.info-tooltip:active .info-tooltip__icon {
    background-color: #007a40;
}

.info-tooltip__body {
    position: fixed;
    z-index: 9999;
    width: max-content;
    max-width: 300px;
    background-color: #2E333F;
    color: #ffffff;
    text-align: left;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 13px;
    line-height: 1.5;
    font-weight: 400;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
    white-space: normal;
    word-wrap: break-word;
}

@media (max-width: 576px) {
    .info-tooltip__body {
        max-width: 90vw;
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(5px);
}
</style>
